import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import { Col, Row, Modal, Form } from 'react-bootstrap';
import moment from 'moment';
import LoadingOverlay from 'react-loading-overlay';
import BounceLoader from 'react-spinners/BounceLoader';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import * as Styles from './style'; // Keep UserBox for consistent padding
import { getAllApiariesService, disableApiary, enableApiary, deleteApiary, updateApiary } from '../../../../services/apiaryService';
import ApiaryMap from './ApiaryMap';
import { Button, ButtonGroup } from 'react-bootstrap';
import LocationResolver from './LocationResolver';
import HouseholdApiariesView from './HouseholdApiariesView';
import DailyApiarySummary from './DailyApiarySummary';
import { Select } from 'antd';
import GenericExportCSVModal from '../shared/GenericExportCSVModal';

const { Option } = Select;

const { SearchBar } = Search;

// --- Summary Dashboard Styled Components ---
const SummaryContainer = styled.div`
    display: flex;
    gap: 20px;
    margin-bottom: 30px;
    flex-wrap: wrap;
`;

const SummaryCard = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 15px;
    min-width: 240px;
    flex: 1;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
    border: 1px solid #f0f0f0;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
    }
`;

const IconWrapper = styled.div`
    width: 50px;
    height: 50px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    background: ${props => props.bg || '#f1f2f6'};
    color: ${props => props.color || '#2d3436'};
`;

const CardContent = styled.div`
    display: flex;
    flex-direction: column;
`;

const CardLabel = styled.span`
    font-size: 13px;
    color: #636e72;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const CardValue = styled.span`
    font-size: 24px;
    color: #2d3436;
    font-weight: 800;
`;

const ApiaryTable = () => {
    const [apiaryData, setApiaryData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [actionLoading, setActionLoading] = useState(null);
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'map'
    const [activeMainTab, setActiveMainTab] = useState('supervised'); // 'supervised' | 'household'
    const [page, setPage] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(1000);
    const [showExportModal, setShowExportModal] = useState(false);

    // Edit modal state
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingApiary, setEditingApiary] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [editSaving, setEditSaving] = useState(false);

    // Filter state
    const [filters, setFilters] = useState({ state: '', district: '', block: '', status: 'ALL', apiary_type: 'ALL', supervisor: '' });
    const [discovery, setDiscovery] = useState({ states: [], districts: [], blocks: [], supervisors: [] });


    const handleToggleActive = async (apiary) => {
        const isCurrentlyActive = apiary.is_active !== false;
        const action = isCurrentlyActive ? 'disable' : 'enable';

        console.log(`🔄 ${action.toUpperCase()} initiated for apiary:`, apiary.apiary_id, apiary.apiary_name);

        const result = await Swal.fire({
            title: isCurrentlyActive ? 'Disable this Apiary?' : 'Enable this Apiary?',
            text: isCurrentlyActive
                ? 'This apiary will be hidden from active lists. You can re-enable it later.'
                : 'This apiary will be visible in active lists again.',
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: isCurrentlyActive ? '#d63031' : '#00b894',
            confirmButtonText: isCurrentlyActive ? 'Yes, Disable' : 'Yes, Enable',
            cancelButtonText: 'Cancel',
        });

        if (!result.value) {
            console.log(`❌ ${action} cancelled by user`);
            return;
        }


        console.log(`⏳ ${action}ing apiary:`, apiary.apiary_id);
        setActionLoading(apiary.apiary_id);

        // Show loading dialog
        Swal.fire({
            title: `${action === 'disable' ? 'Disabling' : 'Enabling'}...`,
            html: `<p>Please wait while we ${action} <b>${apiary.apiary_name}</b></p>`,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            onBeforeOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            if (action === 'disable') {
                const response = await disableApiary(apiary.apiary_id);
                console.log('✅ Disable API response:', response);
                Swal.fire({
                    title: 'Disabled Successfully!',
                    text: `Apiary ${apiary.apiary_name} has been disabled.`,
                    type: 'success',
                    confirmButtonColor: '#00b894',
                    timer: 2000,
                });
                toast.success('Apiary disabled successfully');
            } else {
                const response = await enableApiary(apiary.apiary_id);
                console.log('✅ Enable API response:', response);
                Swal.fire({
                    title: 'Enabled Successfully!',
                    text: `Apiary ${apiary.apiary_name} has been enabled.`,
                    type: 'success',
                    confirmButtonColor: '#00b894',
                    timer: 2000,
                });
                toast.success('Apiary enabled successfully');
            }
            // Refresh the list
            console.log('🔄 Refreshing apiary list...');
            const data = await getAllApiariesService();
            console.log('📋 Updated apiary list:', data.length, 'apiaries');
            setApiaryData(data || []);
            console.log(`✅ Table updated successfully after ${action}`);
        } catch (error) {
            console.error(`❌ ${action} failed:`, error);
            const msg = error.response?.data?.error || `Failed to ${action} apiary`;
            Swal.fire({
                title: `${action === 'disable' ? 'Disable' : 'Enable'} Failed`,
                text: msg,
                type: 'error',
                confirmButtonText: 'OK',
            });
        } finally {
            setActionLoading(null);
            console.log(`🏁 ${action} operation completed`);
        }
    };

    const handleDelete = async (apiary) => {
        console.log('🗑️ Delete initiated for apiary:', apiary.apiary_id, apiary.apiary_name);

        const firstConfirm = await Swal.fire({
            title: 'Delete this Apiary?',
            html: `<p style="color:#636e72">This action is <b>irreversible</b>. The apiary and all associated weather data will be permanently deleted.</p>
                   <p style="font-size:13px;color:#b2bec3">Apiary: <b>${apiary.apiary_name}</b> (${apiary.apiary_id})</p>`,
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d63031',
            confirmButtonText: 'Delete Permanently',
            cancelButtonText: 'Cancel',
        });

        if (!firstConfirm.value) {
            console.log('❌ Delete cancelled by user');
            return;
        }

        console.log('⏳ Deleting apiary:', apiary.apiary_id);
        setActionLoading(apiary.apiary_id);

        // Show loading dialog
        Swal.fire({
            title: 'Deleting...',
            html: `<p>Please wait while we delete <b>${apiary.apiary_name}</b></p>`,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            onBeforeOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            const deleteResponse = await deleteApiary(apiary.apiary_id);
            console.log('✅ Delete API response:', deleteResponse);

            // Show success dialog with checkmark
            await Swal.fire({
                title: 'Deleted Successfully!',
                html: `<p style="color:#00b894">Apiary <b>${apiary.apiary_name}</b> has been permanently deleted.</p>`,
                type: 'success',
                confirmButtonColor: '#00b894',
                confirmButtonText: 'OK',
                timer: 3000,
            });

            toast.success('Apiary deleted permanently');

            // Refresh the list
            console.log('🔄 Refreshing apiary list...');
            const data = await getAllApiariesService();
            console.log('📋 Updated apiary list:', data.length, 'apiaries');
            setApiaryData(data || []);
            // Update discovery options for filters after data refresh
            updateDiscoveryOptions(data || []);
            console.log('✅ Table updated successfully');
        } catch (error) {
            console.error('❌ Delete failed:', error);
            const errData = error.response?.data;
            if (error.response?.status === 409 && errData?.linked_records) {
                const recordsList = errData.linked_records.join(', ');
                console.warn('⚠️ Delete blocked due to linked records:', recordsList);
                Swal.fire({
                    title: 'Cannot Delete',
                    html: `<p>This apiary has linked records that must be removed first:</p>
                           <p style="font-weight:bold;color:#d63031">${recordsList}</p>
                           <p style="font-size:13px;color:#636e72">${errData.suggestion || 'Use Disable instead to soft-delete.'}</p>`,
                    type: 'error',
                    confirmButtonText: 'OK',
                });
            } else {
                console.error('❌ Delete error details:', errData);
                Swal.fire({
                    title: 'Delete Failed',
                    text: errData?.error || 'Failed to delete apiary',
                    type: 'error',
                    confirmButtonText: 'OK',
                });
            }
        } finally {
            setActionLoading(null);
            console.log('🏁 Delete operation completed');
        }
    };

    const handleEdit = (apiary) => {
        setEditingApiary(apiary);
        setEditForm({
            apiary_name: apiary.apiary_name || '',
            state: apiary.state || '',
            district: apiary.district || '',
            block: apiary.block || '',
            village: apiary.village || '',
            supervisor_name: apiary.supervisor_name || '',
            area_hectare: apiary.area_hectare || '',
            apiary_type: apiary.apiary_type || 'Field',
            occupied_hives: apiary.occupied_hives || 0,
            total_hives: apiary.total_hives || 0,
        });
        setEditModalOpen(true);
    };

    const handleEditSubmit = async () => {
        if (!editingApiary) return;

        if (Number(editForm.occupied_hives) > Number(editForm.total_hives)) {
            toast.error('Hives with colonies cannot exceed Total Hives');
            return;
        }

        // Build payload with only changed fields
        const payload = {};
        const orig = editingApiary;
        if (editForm.apiary_name !== (orig.apiary_name || '')) payload.apiary_name = editForm.apiary_name;
        if (editForm.state !== (orig.state || '')) payload.state = editForm.state;
        if (editForm.district !== (orig.district || '')) payload.district = editForm.district;
        if (editForm.block !== (orig.block || '')) payload.block = editForm.block;
        if (editForm.village !== (orig.village || '')) payload.village = editForm.village;
        if (editForm.supervisor_name !== (orig.supervisor_name || '')) payload.supervisor_name = editForm.supervisor_name;
        if (String(editForm.area_hectare) !== String(orig.area_hectare || '')) payload.area_hectare = Number(editForm.area_hectare);
        if (editForm.apiary_type !== (orig.apiary_type || '')) payload.apiary_type = editForm.apiary_type;
        if (Number(editForm.occupied_hives) !== (orig.occupied_hives || 0)) payload.occupied_hives = Number(editForm.occupied_hives);
        if (Number(editForm.total_hives) !== (orig.total_hives || 0)) payload.total_hives = Number(editForm.total_hives);

        if (Object.keys(payload).length === 0) {
            toast.info('No changes detected');
            return;
        }

        setEditSaving(true);
        try {
            await updateApiary(editingApiary.apiary_id, payload);
            toast.success('Apiary updated successfully');
            setEditModalOpen(false);
            // Refresh table
            const data = await getAllApiariesService();
            setApiaryData(data || []);
        } catch (error) {
            const msg = error.response?.data?.error || 'Failed to update apiary';
            toast.error(msg);
        } finally {
            setEditSaving(false);
        }
    };

    const columns = [
        {
            dataField: 'apiary_name',
            text: 'Name',
            sort: true,
            headerStyle: () => ({ width: '200px' }),
            formatter: (cell, row) => (
                <Link to={`/nector-apiaries/${row.apiary_id}`} style={{ fontWeight: 'bold', color: '#ff8701', cursor: 'pointer' }}>
                    {cell}
                </Link>
            ),
        },
        {
            dataField: 'apiary_type',
            text: 'Type',
            sort: true,
            headerStyle: () => ({ width: '100px', textAlign: 'center' }),
            formatter: (cell) => {
                const isStorage = cell === 'Storage';
                return (
                    <div style={{ textAlign: 'center' }}>
                        <span style={{
                            color: isStorage ? '#0c2461' : '#018241',
                            fontWeight: '700',
                            padding: '4px 10px',
                            background: isStorage ? '#d1edff' : '#cdf4de',
                            border: `1px solid ${isStorage ? '#74b9ff' : '#00b894'}`,
                            borderRadius: '12px',
                            fontSize: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'inline-block',
                            minWidth: '75px'
                        }}>
                            {cell || 'FIELD'}
                        </span>
                    </div>
                );
            }
        },
        {
            dataField: 'state',
            text: 'State',
            sort: true,
            headerStyle: () => ({ width: '120px' }),
            formatter: (cell, row) => (
                <LocationResolver
                    lat={row.geo_location?.lat || row.latitude}
                    lng={row.geo_location?.lng || row.longitude}
                    value={cell}
                    field="state"
                    apiaryId={row.apiary_id}
                />
            )
        },
        {
            dataField: 'district',
            text: 'District',
            sort: true,
            headerStyle: () => ({ width: '120px' }),
            formatter: (cell, row) => (
                <LocationResolver
                    lat={row.geo_location?.lat || row.latitude}
                    lng={row.geo_location?.lng || row.longitude}
                    value={cell}
                    field="district"
                    apiaryId={row.apiary_id}
                />
            )
        },
        {
            dataField: 'block',
            text: 'Block',
            sort: true,
            headerStyle: () => ({ width: '120px' }),
            formatter: (cell, row) => (
                <LocationResolver
                    lat={row.geo_location?.lat || row.latitude}
                    lng={row.geo_location?.lng || row.longitude}
                    value={cell}
                    field="block"
                    apiaryId={row.apiary_id}
                />
            )
        },
        {
            dataField: 'village',
            text: 'Village',
            sort: true,
            headerStyle: () => ({ width: '120px' }),
            formatter: (cell, row) => (
                <LocationResolver
                    lat={row.geo_location?.lat || row.latitude}
                    lng={row.geo_location?.lng || row.longitude}
                    value={cell}
                    field="village"
                    apiaryId={row.apiary_id}
                />
            )
        },
        {
            dataField: 'supervisor_name',
            text: 'Supervisor',
            sort: true,
            headerStyle: () => ({ width: '150px' }),
            formatter: (cell) => cell || '-',
        },
        {
            dataField: 'created_by_name',
            text: 'Created By',
            sort: true,
            headerStyle: () => ({ width: '150px' }),
            formatter: (cell, row) => {
                const name = cell || row.created_by?.name || row.created_by?.username || row.created_by_name || '—';
                return (
                    <span style={{ fontWeight: 600, color: '#374151' }}>
                        {name}
                    </span>
                );
            },
        },
        {
            dataField: 'total_hives',
            text: 'Total Hives',
            sort: true,
            headerStyle: () => ({ width: '100px' }),
            formatter: (cell) => cell ?? 0,
        },
        {
            dataField: 'occupied_hives',
            text: 'Hives with colonies',
            sort: true,
            headerStyle: () => ({ width: '120px' }),
            formatter: (cell) => cell ?? 0,
        },
        {
            dataField: 'empty_hives',
            text: 'Hives without colonies',
            sort: true,
            headerStyle: () => ({ width: '120px' }),
            formatter: (cell) => cell ?? 0,
        },
        {
            dataField: 'operational_status',
            text: 'Status',
            sort: true,
            headerStyle: () => ({ width: '100px' }),
            formatter: (cell, row) => {
                if (row.is_active === false) {
                    return <span style={{ color: '#d63031', fontWeight: '600' }}>Disabled</span>;
                }
                return cell || '-';
            },
        },
        {
            dataField: 'created_at',
            text: 'Created On',
            sort: true,
            formatter: (cell) => (cell ? moment(cell).format('DD-MM-YYYY') : '-'),
            headerStyle: () => ({ width: '120px' }),
        },
        {
            dataField: 'actions',
            text: 'Actions',
            isDummyField: true,
            headerStyle: () => ({ width: '180px', textAlign: 'center' }),
            formatter: (cell, row) => {
                const isActive = row.is_active !== false;
                const isProcessing = actionLoading === row.apiary_id;

                return (
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(row);
                            }}
                            disabled={isProcessing}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: '1px solid #74b9ff',
                                fontWeight: '600',
                                fontSize: '12px',
                                cursor: isProcessing ? 'not-allowed' : 'pointer',
                                background: '#dfe6e9',
                                color: '#0984e3',
                                opacity: isProcessing ? 0.6 : 1,
                            }}
                            title="Edit apiary"
                        >
                            <i className="fa fa-pencil" style={{ marginRight: '4px' }}></i>
                            Edit
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleToggleActive(row);
                            }}
                            disabled={isProcessing}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                fontWeight: '600',
                                fontSize: '12px',
                                cursor: isProcessing ? 'not-allowed' : 'pointer',
                                background: isActive ? '#fff5f5' : '#e6fffa',
                                color: isActive ? '#d63031' : '#00b894',
                                border: `1px solid ${isActive ? '#ffa8a8' : '#b2f2bb'}`,
                                opacity: isProcessing ? 0.6 : 1,
                            }}
                            title={isActive ? 'Disable apiary' : 'Enable apiary'}
                        >
                            <i className={`fa ${isActive ? 'fa-ban' : 'fa-check-circle'}`} style={{ marginRight: '4px' }}></i>
                            {isActive ? 'Disable' : 'Enable'}
                        </button>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(row);
                            }}
                            disabled={isProcessing}
                            style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: '1px solid #ffa8a8',
                                fontWeight: '600',
                                fontSize: '12px',
                                cursor: isProcessing ? 'not-allowed' : 'pointer',
                                background: '#d63031',
                                color: '#fff',
                                opacity: isProcessing ? 0.6 : 1,
                            }}
                            title="Delete apiary permanently"
                        >
                            <i className="fa fa-trash" style={{ marginRight: '4px' }}></i>
                            Delete
                        </button>
                    </div>
                );
            },
        },
    ];

    const EXPORT_COLUMNS = [
        { label: 'Name', key: 'apiary_name', path: 'apiary_name' },
        { label: 'Apiary ID', key: 'apiary_id', path: 'apiary_id' },
        { label: 'Type', key: 'apiary_type', formatter: (row) => row.apiary_type || 'FIELD' },
        { label: 'State', key: 'state', path: 'state' },
        { label: 'District', key: 'district', path: 'district' },
        { label: 'Block', key: 'block', path: 'block' },
        { label: 'Village', key: 'village', path: 'village' },
        { label: 'Supervisor', key: 'supervisor_name', formatter: (row) => row.supervisor_name || '-' },
        { label: 'Created By', key: 'created_by_name', formatter: (row) => row.created_by_name || row.created_by?.name || row.created_by?.username || '' },
        { label: 'Total Hives', key: 'total_hives', formatter: (row) => row.total_hives ?? 0 },
        { label: 'Hives with colonies', key: 'occupied_hives', formatter: (row) => row.occupied_hives ?? 0 },
        { label: 'Hives without colonies', key: 'empty_hives', formatter: (row) => row.empty_hives ?? 0 },
        { label: 'Status', key: 'operational_status', formatter: (row) => row.is_active === false ? 'Disabled' : (row.operational_status || '-') },
        { label: 'Created On', key: 'created_at', formatter: (row) => row.created_at ? moment(row.created_at).format('DD-MM-YYYY') : '' }
    ];

    // --- Discovery & Filtering Logic ---
    const updateDiscoveryOptions = (data) => {
        const states = [...new Set(data.map(item => item.state).filter(Boolean))].sort();
        const supervisors = [...new Set(data.map(item => item.supervisor_name).filter(Boolean))].sort();
        setDiscovery(prev => ({ ...prev, states, supervisors }));
    };

    useEffect(() => {
        if (apiaryData.length > 0) {
            updateDiscoveryOptions(apiaryData);
        }
    }, [apiaryData]);

    useEffect(() => {
        const districts = [...new Set(apiaryData
            .filter(item => !filters.state || item.state === filters.state)
            .map(item => item.district)
            .filter(Boolean))].sort();
        setDiscovery(prev => ({ ...prev, districts }));
    }, [filters.state, apiaryData]);

    useEffect(() => {
        const blocks = [...new Set(apiaryData
            .filter(item => (!filters.state || item.state === filters.state) && (!filters.district || item.district === filters.district))
            .map(item => item.block)
            .filter(Boolean))].sort();
        setDiscovery(prev => ({ ...prev, blocks }));
    }, [filters.state, filters.district, apiaryData]);

    const getFilteredData = () => {
        return apiaryData.filter(item => {
            const matchState = !filters.state || item.state === filters.state;
            const matchDistrict = !filters.district || item.district === filters.district;
            const matchBlock = !filters.block || item.block === filters.block;

            let matchStatus = true;
            if (filters.status === 'ACTIVE') {
                matchStatus = item.is_active !== false;
            } else if (filters.status === 'INACTIVE') {
                matchStatus = item.is_active === false;
            }

            const matchType = filters.apiary_type === 'ALL' ||
                (filters.apiary_type === 'Field' && (!item.apiary_type || item.apiary_type === 'Field')) ||
                (item.apiary_type === filters.apiary_type);

            const matchSupervisor = !filters.supervisor || item.supervisor_name === filters.supervisor;

            return matchState && matchDistrict && matchBlock && matchStatus && matchType && matchSupervisor;
        });
    };

    const filteredData = getFilteredData();

    useEffect(() => {
        setPage(1);
    }, [filters]);
    // --- End Discovery & Filtering Logic ---

    // Calculate Summary Stats
    const summaryStats = useMemo(() => {
        return filteredData.reduce((acc, curr) => {
            const hives = curr.hive_count || 0;
            acc.total += hives;
            if (curr.apiary_type === 'Storage') {
                acc.storage += hives;
            } else {
                acc.field += hives;
            }
            return acc;
        }, { total: 0, field: 0, storage: 0 });
    }, [filteredData]);

    useEffect(() => {
        const fetchApiaries = async () => {
            setLoading(true);
            setError('');
            try {
                const data = await getAllApiariesService();
                console.log('✅ Fetched apiaries:', data);
                console.log('✅ Fetched apiaries:', data.filter((item, i) => item?.geo_location?.lat));
                setApiaryData(data || []);
                updateDiscoveryOptions(data || []);
            } catch (err) {
                console.error('❌ Error fetching apiaries:', err);
                setError(err.message || 'Failed to fetch apiaries');
            } finally {
                setLoading(false);
            }
        };

        fetchApiaries();
    }, []);

    const currentPage = Number(page) || 1;
    const currentSize = Number(sizePerPage) || 1000;

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * currentSize;
        return filteredData.slice(start, start + currentSize);
    }, [filteredData, currentPage, currentSize]);

    // Pagination options
    const paginationOptions = {
        custom: true,
        page: currentPage,
        sizePerPage: currentSize,
        totalSize: filteredData.length,
        sizePerPageList: [
            { text: '50', value: 50 },
            { text: '100', value: 100 },
            { text: '500', value: 500 },
            { text: '1000', value: 1000 },
            { text: '2000', value: 2000 },
            { text: '5000', value: 5000 },
            { text: '10000', value: 10000 },
        ],
        onPageChange: (newPage) => {
            setPage(Number(newPage) || 1);
        },
        onSizePerPageChange: (newSize, newPage) => {
            setSizePerPage(Number(newSize) || 1000);
            setPage(Number(newPage) || 1);
        },
        showTotal: true,
    };

    return (
        <Styles.UserBox>
            <div style={{ height: '2vh' }}></div>

            {/* Top 2-Tab Navigation Bar requested by Monika Ma'am */}
            <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '25px',
                background: '#f8fafc',
                padding: '8px',
                borderRadius: '12px',
                border: '1px solid #e2e8f0'
            }}>
                <Button
                    variant={activeMainTab === 'supervised' ? 'primary' : 'light'}
                    style={{
                        borderRadius: '10px',
                        fontWeight: 700,
                        padding: '10px 24px',
                        fontSize: '14px',
                        boxShadow: activeMainTab === 'supervised' ? '0 4px 12px rgba(37,99,235,0.2)' : 'none'
                    }}
                    onClick={() => setActiveMainTab('supervised')}
                >
                    <i className="fa fa-industry mr-2"></i> 1. Supervised / Managed Apiaries
                </Button>
                <Button
                    variant={activeMainTab === 'household' ? 'primary' : 'light'}
                    style={{
                        borderRadius: '10px',
                        fontWeight: 700,
                        padding: '10px 24px',
                        fontSize: '14px',
                        boxShadow: activeMainTab === 'household' ? '0 4px 12px rgba(37,99,235,0.2)' : 'none'
                    }}
                    onClick={() => setActiveMainTab('household')}
                >
                    <i className="fa fa-home mr-2"></i> 2. Beekeeper Household Apiaries
                </Button>
                <Button
                    variant={activeMainTab === 'digest' ? 'primary' : 'light'}
                    style={{
                        borderRadius: '10px',
                        fontWeight: 700,
                        padding: '10px 24px',
                        fontSize: '14px',
                        boxShadow: activeMainTab === 'digest' ? '0 4px 12px rgba(37,99,235,0.2)' : 'none'
                    }}
                    onClick={() => setActiveMainTab('digest')}
                >
                    <i className="fa fa-calendar-check-o mr-2"></i> 3. Daily 9 PM Visit Digest
                </Button>
            </div>

            {activeMainTab === 'household' ? (
                <HouseholdApiariesView isEmbedded={true} />
            ) : activeMainTab === 'digest' ? (
                <DailyApiarySummary isEmbedded={true} />
            ) : (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '20px' }}>
                        <h2 style={{ margin: 0, fontWeight: 800, color: '#2d3436' }}>Apiary Management</h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowExportModal(true)}
                                style={{ fontWeight: 700, borderRadius: '8px' }}
                            >
                                <i className="fa fa-download mr-2"></i> Export CSV
                            </Button>
                            <div style={{ color: '#636e72', fontWeight: '600', fontSize: '14px' }}>
                                Total Apiaries: <span style={{ color: '#ff8701', fontWeight: '800' }}>{filteredData.length}</span>
                            </div>
                        </div>
                    </div>

                    <SummaryContainer>
                        <SummaryCard>
                            <IconWrapper bg="linear-gradient(135deg, #6c5ce7, #a29bfe)" color="#fff">
                                <i className="fa fa-th"></i>
                            </IconWrapper>
                            <CardContent>
                                <CardLabel>On Ground</CardLabel>
                                <CardValue>{summaryStats.total.toLocaleString()}</CardValue>
                            </CardContent>
                        </SummaryCard>

                        <SummaryCard>
                            <IconWrapper bg="linear-gradient(135deg, #00b894, #55efc4)" color="#fff">
                                <i className="fa fa-leaf"></i>
                            </IconWrapper>
                            <CardContent>
                                <CardLabel>In Field</CardLabel>
                                <CardValue>{summaryStats.field.toLocaleString()}</CardValue>
                            </CardContent>
                        </SummaryCard>

                        <SummaryCard>
                            <IconWrapper bg="linear-gradient(135deg, #0984e3, #74b9ff)" color="#fff">
                                <i className="fa fa-warehouse"></i>
                            </IconWrapper>
                            <CardContent>
                                <CardLabel>In Storage</CardLabel>
                                <CardValue>{summaryStats.storage.toLocaleString()}</CardValue>
                            </CardContent>
                        </SummaryCard>
                    </SummaryContainer>

                    {error && (
                        <div style={{ color: 'red', marginBottom: '10px' }}>
                            Error: {error}
                        </div>
                    )}

                    <LoadingOverlay
                        active={loading}
                        spinner={<BounceLoader color={'#E6D825'} />}
                        text='Loading Apiaries...'
                        styles={{
                            overlay: (base) => ({
                                ...base,
                                background: 'rgba(255, 255, 255, 0.7)',
                                zIndex: 100
                            })
                        }}
                    >
                        <ToolkitProvider
                            keyField="apiary_id"
                            data={paginatedData}
                            columns={columns}
                            search
                        >
                            {(toolkitProps) => (
                                <PaginationProvider pagination={paginationFactory(paginationOptions)}>
                                    {({ paginationProps, paginationTableProps }) => (
                                        <>
                                            {/* New Filter Section */}
                                            <Row className="mb-4 p-3" style={{ background: '#f8f9fa', borderRadius: '8px', margin: '0 0 20px 0', border: '1px solid #eee' }}>
                                                <Col md={2}>
                                                    <Form.Label style={{ fontWeight: '600', fontSize: '13px' }}>State</Form.Label>
                                                    <Select
                                                        showSearch
                                                        style={{ width: '100%' }}
                                                        placeholder="All States"
                                                        optionFilterProp="children"
                                                        value={filters.state || undefined}
                                                        onChange={val => setFilters({ ...filters, state: val || '', district: '', block: '' })}
                                                        allowClear
                                                    >
                                                        {discovery.states.map(s => <Option key={s} value={s}>{s}</Option>)}
                                                    </Select>
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Label style={{ fontWeight: '600', fontSize: '13px' }}>District</Form.Label>
                                                    <Select
                                                        showSearch
                                                        style={{ width: '100%' }}
                                                        placeholder="All Districts"
                                                        optionFilterProp="children"
                                                        value={filters.district || undefined}
                                                        onChange={val => setFilters({ ...filters, district: val || '', block: '' })}
                                                        allowClear
                                                        disabled={!filters.state && discovery.districts.length === 0}
                                                    >
                                                        {discovery.districts.map(d => <Option key={d} value={d}>{d}</Option>)}
                                                    </Select>
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Label style={{ fontWeight: '600', fontSize: '13px' }}>Block</Form.Label>
                                                    <Select
                                                        showSearch
                                                        style={{ width: '100%' }}
                                                        placeholder="All Blocks"
                                                        optionFilterProp="children"
                                                        value={filters.block || undefined}
                                                        onChange={val => setFilters({ ...filters, block: val || '' })}
                                                        allowClear
                                                        disabled={!filters.district && discovery.blocks.length === 0}
                                                    >
                                                        {discovery.blocks.map(b => <Option key={b} value={b}>{b}</Option>)}
                                                    </Select>
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Label style={{ fontWeight: '600', fontSize: '13px' }}>Type</Form.Label>
                                                    <Select
                                                        style={{ width: '100%' }}
                                                        value={filters.apiary_type}
                                                        onChange={val => setFilters({ ...filters, apiary_type: val })}
                                                    >
                                                        <Option value="ALL">All Types</Option>
                                                        <Option value="Field">Field</Option>
                                                        <Option value="Storage">Storage</Option>
                                                    </Select>
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Label style={{ fontWeight: '600', fontSize: '13px' }}>Supervisor</Form.Label>
                                                    <Select
                                                        showSearch
                                                        style={{ width: '100%' }}
                                                        placeholder="All Supervisors"
                                                        optionFilterProp="children"
                                                        value={filters.supervisor || undefined}
                                                        onChange={val => setFilters({ ...filters, supervisor: val || '' })}
                                                        allowClear
                                                    >
                                                        {discovery.supervisors.map(s => <Option key={s} value={s}>{s}</Option>)}
                                                    </Select>
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Label style={{ fontWeight: '600', fontSize: '13px' }}>Status</Form.Label>
                                                    <Select
                                                        style={{ width: '100%' }}
                                                        value={filters.status}
                                                        onChange={val => setFilters({ ...filters, status: val })}
                                                    >
                                                        <Option value="ALL">All Status</Option>
                                                        <Option value="ACTIVE">Active</Option>
                                                        <Option value="INACTIVE">Inactive</Option>
                                                    </Select>
                                                </Col>
                                            </Row>

                                            <Row className="mb-3 align-items-center" style={{ justifyContent: 'space-between' }}>
                                                <Col md={4} style={{ marginBottom: '10px' }}>
                                                    <SearchBar
                                                        {...toolkitProps.searchProps}
                                                        placeholder="Search by ID, Name, Supervisor..."
                                                        className="form-control"
                                                    />
                                                </Col>
                                                <Col md="auto" style={{ marginBottom: '10px' }}>
                                                    <ButtonGroup>
                                                        <Button
                                                            variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                                                            onClick={() => setViewMode('list')}
                                                            style={{
                                                                background: viewMode === 'list' ? '#ff8701' : 'transparent',
                                                                borderColor: '#ff8701',
                                                                color: viewMode === 'list' ? '#fff' : '#ff8701'
                                                            }}
                                                        >
                                                            <i className="fa fa-list" style={{ marginRight: '5px' }}></i> List
                                                        </Button>
                                                        <Button
                                                            variant={viewMode === 'map' ? 'primary' : 'outline-primary'}
                                                            onClick={() => setViewMode('map')}
                                                            style={{
                                                                background: viewMode === 'map' ? '#ff8701' : 'transparent',
                                                                borderColor: '#ff8701',
                                                                color: viewMode === 'map' ? '#fff' : '#ff8701'
                                                            }}
                                                        >
                                                            <i className="fa fa-map-marker" style={{ marginRight: '5px' }}></i> Map
                                                        </Button>
                                                    </ButtonGroup>
                                                </Col>
                                            </Row>

                                            {viewMode === 'list' ? (
                                                <>
                                                    <div style={{ overflowX: 'auto', minHeight: '400px' }}>
                                                        <BootstrapTable
                                                            {...toolkitProps.baseProps}
                                                            {...paginationTableProps}
                                                            data={paginatedData}
                                                            striped
                                                            hover
                                                            condensed
                                                            noDataIndication={loading ? " " : "No apiaries found"}
                                                            headerClasses="header-class"
                                                            wrapperClasses="table-responsive"
                                                        />
                                                    </div>

                                                    <Row className="mt-3" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Col md="auto">
                                                            <SizePerPageDropdownStandalone {...paginationProps} />
                                                        </Col>
                                                        <Col md="auto">
                                                            <PaginationListStandalone {...paginationProps} />
                                                        </Col>
                                                    </Row>
                                                </>
                                            ) : (
                                                <div style={{ marginTop: '20px', marginBottom: '40px' }}>
                                                    <ApiaryMap apiaries={toolkitProps.baseProps.data} />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </PaginationProvider>
                            )}
                        </ToolkitProvider>
                    </LoadingOverlay>

                    {/* Edit Apiary Modal */}
                    <Modal show={editModalOpen} onHide={() => setEditModalOpen(false)} size="lg" centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Edit Apiary — {editingApiary?.apiary_id}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Apiary Name</Form.Label>
                                            <Form.Control
                                                value={editForm.apiary_name || ''}
                                                onChange={e => setEditForm({ ...editForm, apiary_name: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Supervisor Name</Form.Label>
                                            <Form.Control
                                                value={editForm.supervisor_name || ''}
                                                onChange={e => setEditForm({ ...editForm, supervisor_name: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>State</Form.Label>
                                            <Form.Control
                                                value={editForm.state || ''}
                                                onChange={e => setEditForm({ ...editForm, state: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>District</Form.Label>
                                            <Form.Control
                                                value={editForm.district || ''}
                                                onChange={e => setEditForm({ ...editForm, district: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Block</Form.Label>
                                            <Form.Control
                                                value={editForm.block || ''}
                                                onChange={e => setEditForm({ ...editForm, block: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Village</Form.Label>
                                            <Form.Control
                                                value={editForm.village || ''}
                                                onChange={e => setEditForm({ ...editForm, village: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Area (Hectare)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={editForm.area_hectare || ''}
                                                onChange={e => setEditForm({ ...editForm, area_hectare: e.target.value })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Apiary Type</Form.Label>
                                            <Form.Control
                                                as="select"
                                                value={editForm.apiary_type || 'Field'}
                                                onChange={e => setEditForm({ ...editForm, apiary_type: e.target.value })}
                                            >
                                                <option value="Field">Field Apiary</option>
                                                <option value="Storage">Storage Apiary</option>
                                            </Form.Control>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Total Hives</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={editForm.total_hives || 0}
                                                onChange={e => setEditForm({ ...editForm, total_hives: Number(e.target.value) })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Hives with colonies</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={editForm.occupied_hives || 0}
                                                onChange={e => setEditForm({ ...editForm, occupied_hives: Number(e.target.value) })}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Hives without colonies (Auto)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                disabled
                                                value={Math.max(0, (Number(editForm.total_hives) || 0) - (Number(editForm.occupied_hives) || 0))}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <button
                                onClick={() => setEditModalOpen(false)}
                                style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleEditSubmit}
                                disabled={editSaving}
                                style={{
                                    padding: '8px 20px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: '#ff8701',
                                    color: '#fff',
                                    fontWeight: '600',
                                    cursor: editSaving ? 'not-allowed' : 'pointer',
                                    opacity: editSaving ? 0.6 : 1,
                                }}
                            >
                                {editSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </Modal.Footer>
                    </Modal>

                    <GenericExportCSVModal
                        show={showExportModal}
                        onHide={() => setShowExportModal(false)}
                        data={filteredData}
                        columns={EXPORT_COLUMNS}
                        title="Export Apiaries to CSV"
                        filenamePrefix="apiaries_export"
                    />
                </>
            )}
        </Styles.UserBox>
    );
};

export default ApiaryTable;
